import React from 'react';
import LegalDocumentLayout from './LegalDocumentLayout';

const TermsOfService = () => (
  <LegalDocumentLayout title="Terms of Service" lastUpdated="24 June 2026">
    <p>
      These Terms of Service govern your access to and use of the TenantScore website,
      mobile application, and related services. By creating an account or using
      TenantScore, you agree to these Terms. If you do not agree, please do not use the Platform.
    </p>

    <h2>1. About TenantScore</h2>
    <p>
      TenantScore is a Kenya-based platform that helps landlords, Airbnb hosts, agents, and tenants manage
      rentals, short-stay bookings, property listings, rent payments, maintenance, and tenant behaviour
      scoring. TenantScore facilitates connections and tools but is not itself a landlord, estate agent, or
      insurer unless explicitly stated in a separate written agreement.
    </p>

    <h2>2. Eligibility and accounts</h2>
    <ul>
      <li>You must be at least 18 years old and able to enter a binding contract.</li>
      <li>You must provide accurate registration details and keep your account credentials secure.</li>
      <li>You are responsible for all activity that occurs under your account.</li>
      <li>
        You must select the correct role (landlord, host, tenant, agent, or other supported role) and use
        features only as permitted for that role.
      </li>
    </ul>

    <h2>3. Listings, applications, and bookings</h2>
    <ul>
      <li>
        Landlords and hosts are responsible for the accuracy of listing information, including price,
        availability, photos, house rules, and legal right to rent or host the property.
      </li>
      <li>
        Tenants and guests must provide truthful information in applications, booking requests, and
        communications.
      </li>
      <li>
        Approval of tenants, guests, or bookings remains the decision of the landlord or host. TenantScore
        tools—including TenantScore ratings—support decisions but do not guarantee any outcome.
      </li>
      <li>
        Separate lease agreements, booking confirmations, or sale arrangements between users are independent
        of TenantScore and must comply with applicable Kenyan property and consumer laws.
      </li>
    </ul>

    <h2>4. TenantScore ratings and reports</h2>
    <p>
      TenantScore may calculate behaviour scores and reports based on platform activity such as payment
      history, property care, communication, and adherence to agreed terms. By using the Platform, you
      acknowledge that:
    </p>
    <ul>
      <li>Scores and reports are informational tools, not legal judgments or credit bureau reports.</li>
      <li>Landlords, hosts, and agents may use this information when evaluating applicants or guests.</li>
      <li>You may dispute inaccurate information through support channels or account features where available.</li>
      <li>TenantScore may update scoring methodology to improve fairness and reliability.</li>
    </ul>

    <h2>5. Payments and fees</h2>
    <ul>
      <li>
        Rent, deposits, booking fees, and other amounts are agreed between users unless TenantScore
        explicitly processes a payment as a platform service.
      </li>
      <li>
        Where payment evidence or rent tracking is uploaded, users must submit genuine records. False or
        misleading payment information is prohibited.
      </li>
      <li>
        Any subscription, service, or platform fees will be disclosed before you incur them. Pricing shown on
        the marketing site or in your dashboard applies at the time of purchase or renewal.
      </li>
    </ul>

    <h2>6. Acceptable use</h2>
    <p>You agree not to:</p>
    <ul>
      <li>Post fraudulent, misleading, discriminatory, or unlawful listings or content</li>
      <li>Harass, threaten, or abuse other users or TenantScore staff</li>
      <li>Attempt to bypass security, scrape data without permission, or interfere with Platform operations</li>
      <li>Use the Platform for money laundering, fraud, or any illegal purpose</li>
      <li>Impersonate another person or misrepresent your authority to act for a property owner</li>
      <li>Circumvent TenantScore to avoid applicable fees where platform tools are required by agreement</li>
    </ul>

    <h2>7. Agent and admin access</h2>
    <p>
      Property agents and system administrators may access data necessary to manage assigned properties,
      support users, or maintain platform integrity. Such access is limited to legitimate business purposes
      and subject to role-based permissions and audit controls where implemented.
    </p>

    <h2>8. Intellectual property</h2>
    <p>
      TenantScore owns the Platform software, branding, and design. You retain ownership of content you
      upload (such as listing photos and descriptions) but grant TenantScore a non-exclusive licence to
      display, store, and distribute that content solely to operate and promote the Platform.
    </p>

    <h2>9. Third-party services</h2>
    <p>
      The Platform may link to or integrate with third-party services (for example maps, messaging, or
      payment providers). Your use of those services is governed by their own terms and privacy policies.
      TenantScore is not responsible for third-party services outside our reasonable control.
    </p>

    <h2>10. Disclaimers</h2>
    <p>
      The Platform is provided on an &quot;as is&quot; and &quot;as available&quot; basis. To the fullest extent
      permitted by law, TenantScore disclaims warranties regarding uninterrupted service, listing accuracy,
      suitability of tenants or guests, property condition, or financial outcomes from any rental or booking.
    </p>

    <h2>11. Limitation of liability</h2>
    <p>
      To the maximum extent permitted by Kenyan law, TenantScore and its officers, employees, and partners
      will not be liable for indirect, incidental, special, or consequential damages arising from your use of
      the Platform, disputes between users, property damage, unpaid rent, booking cancellations, or data loss.
      Our total liability for any claim relating to the Platform will not exceed the amount you paid
      TenantScore for platform services in the twelve (12) months before the claim, or KES 10,000 if no
      such fees were paid.
    </p>

    <h2>12. Suspension and termination</h2>
    <p>
      We may suspend or terminate accounts that violate these Terms, pose security risks, or harm other
      users. You may close your account at any time through account settings or by contacting support.
      Provisions that by nature should survive termination—including liability limits, dispute resolution,
      and intellectual property—will continue to apply.
    </p>

    <h2>13. Changes to these Terms</h2>
    <p>
      We may update these Terms from time to time. Material changes will be posted on this page with an
      updated date. Continued use after changes become effective constitutes acceptance of the revised Terms.
    </p>

    <h2>14. Governing law and disputes</h2>
    <p>
      These Terms are governed by the laws of Kenya. Parties agree to attempt good-faith resolution of
      disputes before pursuing formal legal action. Courts in Kenya shall have jurisdiction unless mandatory
      law provides otherwise.
    </p>

    <h2>15. Contact</h2>
    <p>For questions about these Terms, contact TenantScore:</p>
    <ul>
      <li>Phone: +254 113 886 814</li>
      <li>Website: tenantscore.co.ke</li>
    </ul>
  </LegalDocumentLayout>
);

export default TermsOfService;
